import { Link } from "react-router-dom";
import { UserCheck, Target, Award, Users, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SPORTS, SPORT_ICONS } from "@/data/sports";

const HERO_IMG = "https://images.unsplash.com/photo-1649789248266-ef1c7f744f6f?crop=entropy&cs=srgb&fm=jpg&q=85";

const VALUE_PROPS = [
  { title: "Get Matched", tagline: "Find the right partner", desc: "Smart matching by activity, skill level, location, and schedule for the perfect partner fit.", Icon: UserCheck },
  { title: "Stay Accountable", tagline: "Track progress together", desc: "Shared logs, check-ins and streaks that keep motivation high every single week.", Icon: Target },
  { title: "Earn Rewards", tagline: "Consistency pays", desc: "Unlock points, streak badges and partner perks as you hit your goals.", Icon: Award },
  { title: "Join a Community", tagline: "People. Sports. Progress.", desc: "Connect with local sports groups, challenges and fellow fitness enthusiasts.", Icon: Users },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center fl-grid-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0A0A0B] to-black" />
        <img src={HERO_IMG} alt="Athletes training together" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/70 to-transparent" />
        <div className="absolute -right-40 top-1/4 w-96 h-96 bg-[#E53E3E]/20 rounded-full blur-[120px]" />

        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full pt-28 pb-20">
          <div className="max-w-3xl">
            <span className="fl-animate inline-block text-xs font-bold uppercase tracking-widest text-[#E53E3E] mb-5">
              Fitness • Sports • Accountability
            </span>
            <h1 className="fl-animate font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.95]" style={{ animationDelay: "0.1s" }}>
              More Than a Workout.<br /><span className="text-[#E53E3E]">A Stronger You.</span>
            </h1>
            <p className="fl-animate mt-6 text-lg text-zinc-400 max-w-2xl leading-relaxed" style={{ animationDelay: "0.2s" }}>
              FitLynk connects people through fitness and sports. Find your accountability partner, stay consistent, achieve your goals and enjoy the journey together.
            </p>
            <div className="fl-animate mt-9 flex flex-col sm:flex-row gap-4" style={{ animationDelay: "0.3s" }}>
              <Link to="/get-started">
                <Button size="lg" className="w-full sm:w-auto bg-[#E53E3E] hover:bg-[#C53030] rounded-full text-base font-semibold h-14 px-8 fl-red-glow" data-testid="hero-get-started-cta">
                  Get Started <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/sports">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-zinc-700 bg-white/5 backdrop-blur hover:bg-white hover:text-black rounded-full text-base font-semibold h-14 px-8" data-testid="hero-explore-sports-cta">
                  Explore Sports
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="py-16 md:py-24 relative">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-[#E53E3E]">Why FitLynk</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight">Built to keep you consistent</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUE_PROPS.map(({ title, tagline, desc, Icon }, i) => (
              <div
                key={title}
                className="group relative rounded-xl border border-zinc-800 bg-zinc-900/60 p-7 hover:border-[#E53E3E]/60 hover:-translate-y-1 transition-all duration-300"
                data-testid={`value-prop-${i}`}
              >
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#E53E3E]/10 text-[#E53E3E] group-hover:bg-[#E53E3E] group-hover:text-white transition-colors">
                  <Icon className="w-6 h-6" />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight">{title}</h3>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#E53E3E] mt-1">{tagline}</p>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPORTS STRIP */}
      <section className="py-16 md:py-24 bg-black/40 border-y border-zinc-900">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#E53E3E]">One platform. Every sport.</span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">Pick your activity</h2>
            </div>
            <Link to="/sports" className="inline-flex items-center text-sm font-semibold text-zinc-300 hover:text-[#E53E3E] transition-colors">
              View all sports <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {SPORTS.map((s) => {
              const Icon = SPORT_ICONS[s.slug];
              return (
                <div key={s.slug} className="group flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-5 hover:border-[#E53E3E]/60 transition-colors" data-testid={`home-sport-${s.slug}`}>
                  <span className="grid place-items-center w-10 h-10 rounded-lg bg-zinc-800 text-[#E53E3E] group-hover:scale-110 transition-transform">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="font-display font-bold uppercase tracking-tight">{s.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-black p-10 sm:p-16 text-center fl-noise">
            <div className="absolute -left-20 -top-20 w-72 h-72 bg-[#E53E3E]/20 rounded-full blur-[100px]" />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tight">Your partner is waiting.</h2>
              <p className="mt-4 text-zinc-400 max-w-xl mx-auto">Join FitLynk today and never train alone again. Stay consistent, hit your goals, and enjoy the journey together.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/get-started">
                  <Button size="lg" className="w-full sm:w-auto bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-14 px-8">Join FitLynk</Button>
                </Link>
                <Link to="/how-it-works">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-zinc-700 bg-transparent hover:bg-white hover:text-black rounded-full font-semibold h-14 px-8">How It Works</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
